import React from 'react';
import { 
  Phone, MessageSquare, Shield, HelpCircle, 
  Mail, Clock, AlertCircle 
} from 'lucide-react';
import { useAuth } from '../components/AuthContext';

const HelpPage = () => {
  const { user } = useAuth();
  const isPatient = user?.role === 'USER';
  const isDoctor = user?.role === 'DOCTOR';

  const supportOptions = [
    {
      icon: Phone,
      title: 'Call Support',
      description: 'Speak with our care team',
      action: 'tel:1-800-CARE-NOW',
      label: '1-800-CARE-NOW',
      available: '24/7'
    },
    {
      icon: MessageSquare,
      title: 'Text Support',
      description: 'Quick text response',
      action: 'sms:1-800-CARE-NOW?body=I need help with SymptoConnect',
      label: 'Send Text',
      available: '24/7'
    },
    {
      icon: Mail,
      title: 'Email Support',
      description: 'For non-urgent questions',
      action: 'mailto:support@symptoconnect.com',
      label: 'support@symptoconnect.com',
      available: 'Response within 24h'
    }
  ];

  const faqs = isPatient ? [
    {
      q: 'How do I report a new symptom?',
      a: 'Tap "Report an Issue" on your dashboard and select the appropriate category. You can also use voice input if needed.'
    },
    {
      q: 'What do the mood check-ins do?',
      a: 'They help your care team understand how you\'re feeling. If you select "Need Help," they\'ll be notified immediately.'
    },
    {
      q: 'How do I respond to my care team?',
      a: 'Check "Actions From Your Care Team" on your dashboard and tap any pending action to respond.'
    },
    {
      q: 'Is my data secure?',
      a: 'Yes. We use HIPAA-compliant encryption and security. You control who can see your health data.'
    }
  ] : [
    {
      q: 'How do I view my priority queue?',
      a: 'The Priority Queue is shown on your home dashboard. Critical items appear at the top.'
    },
    {
      q: 'How do I view my on-call schedule?',
      a: 'Go to the Schedule page from the sidebar to see your on-call status and upcoming shifts. On-call rotations are managed by your system administrator.'
    },
    {
      q: 'What do risk scores mean?',
      a: 'Risk scores combine multiple factors (vitals, symptoms, medication adherence) to prioritize patient outreach.'
    },
    {
      q: 'How do I execute actions?',
      a: 'Click "Execute" on any action card. This creates a care event and notifies the patient.'
    }
  ];

  return (
    <div className="space-y-6 py-6">
      {/* Emergency Notice */}
      <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-bold text-red-900">Medical Emergency?</p>
          <p className="text-sm text-red-700">
            If this is a life-threatening emergency, call 911 immediately. Do not wait for a response here.
          </p>
        </div>
      </div>

      {/* Support Options */}
      <div className="space-y-3">
        <h2 className="font-bold text-slate-900">Contact Support</h2>
        {supportOptions.map((option) => (
          <a
            key={option.title}
            href={option.action}
            className="block bg-white rounded-2xl p-4 border border-slate-200 hover:border-primary/50 hover:shadow-sm transition-all"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <option.icon className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-slate-900">{option.title}</h3>
                <p className="text-sm text-slate-500">{option.description}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-primary">{option.label}</p>
                <p className="text-xs text-slate-500 flex items-center gap-1 justify-end">
                  <Clock className="w-3 h-3" />
                  {option.available}
                </p>
              </div>
            </div>
          </a>
        ))}
      </div>

      {/* FAQs */}
      <div className="space-y-3">
        <h2 className="font-bold text-slate-900">Frequently Asked Questions</h2>
        {faqs.map((faq, index) => (
          <div key={index} className="bg-white rounded-2xl p-4 border border-slate-200">
            <h3 className="font-bold text-slate-900 flex items-start gap-2">
              <HelpCircle className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
              {faq.q}
            </h3>
            <p className="text-sm text-slate-600 mt-2 pl-6">{faq.a}</p>
          </div>
        ))}
      </div>

      {/* Privacy Notice */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-start gap-3">
        <Shield className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-bold text-emerald-900">Your Privacy Matters</p>
          <p className="text-sm text-emerald-700">
            All communications are HIPAA-compliant and encrypted. We never share your data with third parties.
          </p>
        </div>
      </div>

      {/* Version */}
      <p className="text-center text-xs text-slate-500">
        SymptoConnect v1.0.0
      </p>
    </div>
  );
};

export default HelpPage;
